import React from 'react';
import Icon from '../../../components/AppIcon';

const TimelineView = ({ auditLogs, loading }) => {
  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'created':
        return { name: 'PlusCircle', color: 'text-green-600', bgColor: 'bg-green-50' };
      case 'updated':
        return { name: 'Edit', color: 'text-yellow-600', bgColor: 'bg-yellow-50' };
      case 'deleted':
        return { name: 'Trash2', color: 'text-red-600', bgColor: 'bg-red-50' };
      default:
        return { name: 'FileText', color: 'text-gray-600', bgColor: 'bg-gray-50' };
    }
  };

  const formatEntityType = (entityType) => {
    return entityType?.split('_')?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(' ');
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    let relativeTime = '';
    if (diffMins < 1) {
      relativeTime = 'Just now';
    } else if (diffMins < 60) {
      relativeTime = `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      relativeTime = `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      relativeTime = `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else {
      relativeTime = date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }

    return {
      relative: relativeTime,
      absolute: date?.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    };
  };

  const groupByDate = (logs) => {
    const groups = {};
    logs?.forEach(log => {
      const date = new Date(log?.timestamp)?.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
      if (!groups?.[date]) {
        groups[date] = [];
      }
      groups?.[date]?.push(log);
    });
    return groups;
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg elevation-sm p-8 text-center">
        <Icon name="Loader2" size={32} className="animate-spin text-muted-foreground mx-auto mb-3" />
        <p className="text-muted-foreground">Loading timeline...</p>
      </div>
    );
  }

  if (!auditLogs || auditLogs?.length === 0) {
    return (
      <div className="bg-card rounded-lg elevation-sm p-8 text-center">
        <Icon name="Clock" size={48} className="text-muted-foreground mx-auto mb-3" />
        <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
          No Timeline Data
        </h3>
        <p className="text-muted-foreground">
          No audit entries match your current filters. Try adjusting your search criteria.
        </p>
      </div>
    );
  }

  const groupedLogs = groupByDate(auditLogs);

  return (
    <div className="bg-card rounded-lg elevation-sm p-4 md:p-6">
      <div className="space-y-8">
        {Object.entries(groupedLogs)?.map(([date, logs]) => (
          <div key={date}>
            {/* Date Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="Calendar" size={20} className="text-primary" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground">{date}</h3>
              <div className="flex-1 h-px bg-border"></div>
            </div>

            {/* Timeline Items */}
            <div className="ml-6 border-l-2 border-border pl-6 space-y-6">
              {logs?.map((log, index) => {
                const actionIcon = getActionIcon(log?.actionType);
                const timestamp = formatTimestamp(log?.timestamp);

                return (
                  <div key={log?.id} className="relative">
                    {/* Timeline Dot */}
                    <div className="absolute -left-[29px] top-2">
                      <div className={`w-4 h-4 rounded-full border-2 border-background ${actionIcon?.bgColor}`}></div>
                    </div>

                    {/* Timeline Card */}
                    <div className="bg-background rounded-lg border border-border p-4 hover:border-primary/50 transition-smooth">
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`${actionIcon?.bgColor} ${actionIcon?.color} p-2 rounded-lg`}>
                            <Icon name={actionIcon?.name} size={18} />
                          </div>
                          <div>
                            <h4 className="font-heading font-semibold text-foreground">
                              {log?.changeSummary}
                            </h4>
                            <p className="text-sm text-muted-foreground mt-1">
                              {formatEntityType(log?.entityType)}
                              {log?.entityName && ` • ${log?.entityName}`}
                            </p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-medium text-foreground">{timestamp?.relative}</p>
                          <p className="text-xs text-muted-foreground mt-1">{timestamp?.absolute}</p>
                        </div>
                      </div>

                      {/* User Info */}
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Icon name="User" size={14} />
                        <span>{log?.userName || 'System'}</span>
                        {log?.userEmail && (
                          <>
                            <span>•</span>
                            <span>{log?.userEmail}</span>
                          </>
                        )}
                      </div>

                      {/* Additional Metadata */}
                      {(log?.ipAddress || log?.sessionId) && (
                        <div className="mt-3 pt-3 border-t border-border flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                          {log?.ipAddress && (
                            <div className="flex items-center gap-1">
                              <Icon name="Globe" size={12} />
                              <span>IP: {log?.ipAddress}</span>
                            </div>
                          )}
                          {log?.sessionId && (
                            <div className="flex items-center gap-1">
                              <Icon name="Key" size={12} />
                              <span>Session: {log?.sessionId?.substring(0, 8)}...</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TimelineView;